<!-- eslint-disable tailwindcss/no-custom-classname -->
<template>
	<div ref="imagesContainer" class="py-5 md:py-10">
		<div
			v-if="status === 'pending' && allItems.length === 0"
			class="flex justify-center"
		>
			<v-spinner size="lg" />
		</div>

		<v-info v-else-if="allItems.length === 0" class="text-center text-gray-600">
			{{ $t("reviews.no-reviews") }}
		</v-info>

		<template v-else>
			<!-- Show some user-generated pictures from the reviews here. Note
            that only a few are rendered right away, but this still includes
            all the review images for the product because lightbox uses their
            CSS class to find which images to render inside its modal -->
			<div
				v-if="images.length > 0"
				class="no-shrink relative mb-8 flex min-h-25 max-w-112 gap-4 overflow-hidden"
			>
				<a
					v-for="(item, index) in images"
					:key="item.src"
					target="_blank"
					:href="getRezisedImageUrl(item.src, 600)"
					class="review-image relative z-10 flex shrink-0"
					:class="{
						hidden: index >= 3,
					}"
					data-pswp-type="image"
					:data-pswp-height="600"
					:data-pswp-width="600"
					tabindex="-1"
				>
					<!-- eslint-disable-next-line vuejs-accessibility/media-has-caption -->
					<img
						:key="item.src"
						class="size-[100px] rounded"
						:src="getRezisedImageUrl(item.src, 100)"
						loading="lazy"
						alt="Review photo"
					/>

					<!-- Hidden content used in the lightbox modal to show related review info below the image -->
					<div class="hidden-caption-content hidden">
						<div
							class="pt-2 text-black hover:cursor-pointer md:grid md:grid-cols-4 md:divide-x"
						>
							<div class="space-y-1 py-1">
								<p
									v-if="item.review.isVerifiedBuyer"
									class="flex h-6 items-center space-x-1 text-gray-600"
								>
									<span
										class="flex size-4 items-center justify-center rounded-full bg-green-600 text-xs text-white"
									>
										<v-icon icon="check" />
									</span>

									<span class="text-sm">
										{{ $t("reviews.verified-customer") }}
									</span>
								</p>

								<p class="font-bold">
									{{ item.review.author }}
								</p>
							</div>

							<div class="py-1 md:col-span-3 md:px-5">
								<div class="mb-4 flex h-4 items-center space-x-px">
									<v-icon
										v-for="i in 5"
										:key="i"
										:icon="getIcon(i, item.review.rating)"
									/>
								</div>

								<p class="review-content review-content--closed z-999">
									{{ item.review.content }}
								</p>

								<p class="mt-4 text-right text-xs text-gray-600">
									{{ formatDistance(item.review.date) }}
								</p>
							</div>
						</div>
					</div>
				</a>

				<button
					v-if="images.length > 3"
					class="flex size-24 flex-none items-center justify-center rounded border text-lg font-bold hover:cursor-pointer hover:bg-gray-100"
					type="button"
					@click="handleClick({position: 0})"
				>
					+{{ images.length - 3 }}
				</button>
			</div>

			<hr v-if="images.length > 0" class="my-5 md:my-10" />

			<!-- Actual reviews -->
			<template v-for="(review, index) in allItems" :key="review.id">
				<hr v-if="index > 0" class="my-5 md:my-10" />

				<div class="md:grid md:grid-cols-4 md:divide-x">
					<div class="space-y-1 py-1">
						<p
							v-if="review.isVerifiedBuyer"
							class="flex h-6 items-center space-x-2 text-gray-600"
						>
							<span
								class="flex size-4 items-center justify-center rounded-full bg-green-600 text-xs text-white"
							>
								<v-icon icon="check" />
							</span>

							<span class="text-sm">
								{{ $t("reviews.verified-customer") }}
							</span>
						</p>

						<p class="font-bold">
							{{ review.author }}
						</p>
					</div>

					<div class="py-1 md:col-span-3 md:px-5">
						<div class="mb-4 flex h-6 items-center space-x-px">
							<v-icon
								v-for="i in 5"
								:key="i"
								:icon="getIcon(i, review.rating)"
							/>
						</div>

						<p>{{ review.content }}</p>

						<div
							v-if="review.photos.length > 0"
							class="relative mt-4 flex gap-4 overflow-hidden bg-gray-50 md:bg-transparent"
						>
							<!-- eslint-disable-next-line vuejs-accessibility/click-events-have-key-events -->
							<button
								v-for="(photo, nthImage) in review.photos"
								:key="photo"
								type="button"
								class="w-auto shrink-0 hover:cursor-zoom-in"
								@click="handleClick({review, nthImage})"
							>
								<img
									class="size-24 rounded"
									:src="getRezisedImageUrl(photo, 100)"
									loading="lazy"
									alt="Review photo"
								/>
							</button>
						</div>

						<p class="mt-6 text-right text-xs text-gray-600">
							{{ formatDistance(review.date) }}
						</p>
					</div>
				</div>
			</template>

			<div class="mx-auto mt-20 w-full space-y-8 md:w-2/3">
				<v-button
					class="w-full"
					:theme="!areAllLoaded ? 'black' : 'gray'"
					:disabled="areAllLoaded"
					@click="currentPage += 1"
				>
					{{
						areAllLoaded
							? $t("reviews.loader.show-more-reviews-loaded")
							: $t("reviews.loader.show-more")
					}}
				</v-button>

				<v-progress-bar :total="totalCount" :current="allItems.length">
					{{
						$t("reviews.loader.show-more-count", {
							current: allItems.length,
							total: totalCount,
						})
					}}
				</v-progress-bar>
			</div>
		</template>
	</div>
</template>

<script lang="ts" setup>
import {whenever} from "@vueuse/core";
import PhotoSwipe from "photoswipe";
import type {Review} from "@pkg/shared/server/api/reviews/reviews.get";

import {useDate} from "@pkg/shared/composables/use-date";
import {useGallery} from "@pkg/shared/composables/use-gallery";
import {useReviews} from "@pkg/shared/composables/use-reviews";

const properties = defineProps<{
	skus: string[];
}>();

const {getReviews, getReviewsWithImages, getIcon, getRezisedImageUrl} =
	useReviews();

const {formatDistance} = useDate();

const currentPage = ref(1);

// All the product's review images, needed for lightbox
const {images} = getReviewsWithImages(properties.skus);

const {reviews, totalCount, status} = getReviews(properties.skus, currentPage);

const allItems = ref<typeof reviews.value>([]);
const areAllLoaded = computed(() => allItems.value.length >= totalCount.value);

whenever(reviews, (value) => {
	allItems.value = [...allItems.value, ...value];
});

const imagesContainer = ref<HTMLDivElement>();

// Non-dynamic semi-hack to use smaller paddings on mobile since the mobile version doesn't display back/next buttons so there's no need to have much horiz padding.
const lightboxXPadding =
	import.meta.client && window.innerWidth < 768 ? 20 : 80;

const lightBoxParameters = computed(() => ({
	gallery: imagesContainer.value,
	// Lightbox turns elements that match this query selector into gallery elements, makes them clickable and fetches their display info from their data attributes.
	children: "a.review-image",
	pswpModule: PhotoSwipe,

	showHideAnimationType: "fade" as const,
	bgOpacity: 1,
	padding: {
		top: 80,
		right: lightboxXPadding,
		// Add good amount of spacing to the bottom to prevent review content from being to cramped
		bottom: 160,
		left: lightboxXPadding,
	},
}));

const {lightBox} = useGallery({lightBoxParameters});

watch(lightBox, () => {
	if (lightBox.value) {
		lightBox.value.on("uiRegister", () => {
			lightBox.value?.pswp?.ui?.registerElement({
				name: "custom-caption",
				isButton: false,
				appendTo: "root",
				html: "",
				onClick: (_event, el) => {
					if (!el) {
						return;
					}

					el.classList.toggle("open");
					const reviewContentEl = el.querySelector(".review-content");
					if (reviewContentEl) {
						reviewContentEl.classList.toggle("review-content--closed");
					}
				},
				onInit: (el) => {
					lightBox.value?.pswp?.on("change", () => {
						const currSlideElement =
							lightBox.value?.pswp?.currSlide?.data.element;
						let captionHTML = "";
						if (currSlideElement) {
							const hiddenCaption = currSlideElement.querySelector(
								".hidden-caption-content",
							);
							captionHTML = hiddenCaption ? hiddenCaption.getHTML() : "";
						}
						el.innerHTML = captionHTML || "";
					});
				},
			});
		});
	}
});

const getNthImage = (review: Review, nthImage: number) =>
	images.value.findIndex(
		(img) => img.review.id === review.id && img.position === nthImage,
	);

const handleClick = ({
	position,
	review,
	nthImage,
}: {
	position?: number;
	review?: Review;
	nthImage?: number;
}) => {
	if (!lightBox.value) {
		return;
	}

	let pos = 0;
	if (position) {
		pos = position;
	} else if (review && typeof nthImage === "number") {
		pos = getNthImage(review, nthImage);
	}

	lightBox.value.loadAndOpen(pos);
};
</script>

<style lang="scss">
.pswp__custom-caption {
	background: white;
	width: calc(100% - 2rem);
	left: 50%;
	bottom: 1rem;
	max-width: 700px;
	padding: 0.5rem 1rem;
	position: absolute;
	transform: translateX(-50%);
}

.review-content {
	min-height: 1.5rem;
}
.review-content--closed {
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
	border: none;
}
</style>
